import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { modifier } from 'ember-modifier';
import { eq } from 'tio-ui/utilities';
import { PhDotsThree } from 'ember-phosphor-icons';
import Popover from './popover';
import Select from './select';
import type RouterService from '@ember/routing/router-service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NavTab {
  /** Display label for the tab */
  label: string;
  /** Ember route path */
  route: string;
  /** Optional model param passed to LinkTo */
  model?: unknown;
}

interface NavTabsSignature {
  Args: {
    /** Array of tab definitions */
    tabs: NavTab[];
    /** When true, pins the tabs to the top of the viewport on scroll with an opaque background. */
    sticky?: boolean;
  };
  Element: HTMLDivElement;
}

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------

const TAB_BASE =
  'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors duration-150';

const TAB_INACTIVE =
  'border-transparent tio-fg-muted hover:tio-border-default hover:tio-fg-secondary';

// Routes through --brand (tio-purple-600 light / tio-purple-300 dark) so
// the active tab keeps brand contrast in both modes — a raw violet-600
// atom doesn't lift in dark mode and reads too dark.
const TAB_ACTIVE = 'border-[var(--brand)] text-[var(--brand)]';

// ---------------------------------------------------------------------------
// NavTabs component
// ---------------------------------------------------------------------------

/**
 * Reimagined NavTabs — route-based navigation tabs with responsive overflow.
 *
 * Features:
 * - Route-based tabs via `<LinkTo>`
 * - Responsive overflow menu via reimagined Popover when tabs don't fit
 * - Mobile: collapses to a reimagined Select for small screens
 * - Matches reimagined design system (violet accent)
 *
 * @example
 * ```gts
 * import NavTabs from 'tio-ui/components/nav-tabs';
 * import { array, hash } from '@ember/helper';
 *
 * <NavTabs
 *   @tabs={{array
 *     (hash label="Overview" route="authenticated.programs.show")
 *     (hash label="Details" route="authenticated.programs.details")
 *     (hash label="History" route="authenticated.programs.history")
 *   }}
 * />
 * ```
 */
class NavTabs extends Component<NavTabsSignature> {
  @service declare router: RouterService;
  @tracked visibleCount = 0;
  @tracked hasMeasured = false;
  tabWidths: number[] = [];
  navElement?: HTMLElement;
  resizeObserver?: ResizeObserver;

  // ── Modifiers ───────────────────────────────────────────────────

  setupNav = modifier((element: HTMLElement) => {
    this.navElement = element;

    // Use rAF to ensure DOM is painted before measuring
    requestAnimationFrame(() => {
      this.measureAndCalculate();
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.calculateVisibleTabs();
    });
    this.resizeObserver.observe(element);

    return () => {
      this.resizeObserver?.disconnect();
    };
  });

  // ── Measurement ─────────────────────────────────────────────────

  measureAndCalculate = () => {
    if (!this.navElement) return;

    const tabElements = this.navElement.querySelectorAll<HTMLElement>('[data-nav-tab]');
    this.tabWidths = Array.from(tabElements).map((el) => {
      const style = window.getComputedStyle(el);
      const marginLeft = Number.parseFloat(style.marginLeft) || 0;
      const marginRight = Number.parseFloat(style.marginRight) || 0;
      return el.offsetWidth + marginLeft + marginRight;
    });

    this.hasMeasured = true;
    this.calculateVisibleTabs();
  };

  calculateVisibleTabs = () => {
    if (!this.navElement || !this.hasMeasured) return;

    const containerWidth = this.navElement.offsetWidth;
    const overflowButtonWidth = 48;
    const gapWidth = 32; // gap-x-8 = 2rem
    let availableWidth = containerWidth - overflowButtonWidth;
    let count = 0;

    for (let i = 0; i < this.tabWidths.length; i++) {
      const tabWidth = this.tabWidths[i] ?? 0;
      const gapNeeded = i > 0 ? gapWidth : 0;
      if (availableWidth >= tabWidth + gapNeeded) {
        availableWidth -= tabWidth + gapNeeded;
        count++;
      } else {
        break;
      }
    }

    // If all tabs fit without an overflow button, show them all
    const totalWidth = this.tabWidths.reduce((sum, w, i) => sum + w + (i > 0 ? gapWidth : 0), 0);
    if (totalWidth <= containerWidth) {
      count = this.args.tabs.length;
    }

    // Always show at least 1 tab
    if (count === 0 && this.args.tabs.length > 0) {
      count = 1;
    }

    this.visibleCount = count;
  };

  // ── Actions ─────────────────────────────────────────────────────

  handleSelectChange = (key: string | null) => {
    if (!key) return;
    const tab = this.args.tabs.find((t) => t.route === key);
    if (tab) {
      if (tab.model) {
        this.router.transitionTo(tab.route, tab.model as object);
      } else {
        this.router.transitionTo(tab.route);
      }
    }
  };

  handleOverflowClick = (tab: NavTab) => {
    if (tab.model) {
      this.router.transitionTo(tab.route, tab.model as object);
    } else {
      this.router.transitionTo(tab.route);
    }
  };

  // ── Derived state ───────────────────────────────────────────────

  get currentRoute() {
    return this.router.currentRouteName;
  }

  get selectItems() {
    return this.args.tabs.map((tab) => ({
      key: tab.route,
      label: tab.label,
    }));
  }

  get visibleTabs() {
    // Before measurement, show all so we can measure them
    if (!this.hasMeasured) {
      return this.args.tabs;
    }
    return this.args.tabs.slice(0, this.visibleCount);
  }

  get overflowTabs() {
    if (!this.hasMeasured) {
      return [];
    }
    return this.args.tabs.slice(this.visibleCount);
  }

  get hasOverflow() {
    return this.hasMeasured && this.overflowTabs.length > 0;
  }

  // ── Template ────────────────────────────────────────────────────

  <template>
    <div class="w-full {{if @sticky 'sticky top-0 z-30 tio-bg-surface'}}" ...attributes>
      {{! ── Mobile: Select dropdown (sm and below) ── }}
      <div class="sm:hidden border-b tio-border-default pb-4">
        <Select
          @items={{this.selectItems}}
          @selected={{this.currentRoute}}
          @onChange={{this.handleSelectChange}}
          @size="md"
          @clearable={{false}}
        />
      </div>

      {{! ── Desktop: Tabs with overflow (sm+) ── }}
      <div class="hidden sm:block border-b tio-border-default">
        <nav class="-mb-px flex items-center gap-x-8" aria-label="Tabs" {{this.setupNav}}>
          {{#each this.visibleTabs as |tab|}}
            {{#if tab.model}}
              <LinkTo
                @route={{tab.route}}
                @model={{tab.model}}
                class="{{TAB_BASE}} {{if (eq tab.route this.currentRoute) TAB_ACTIVE TAB_INACTIVE}}"
                data-nav-tab
              >
                {{tab.label}}
              </LinkTo>
            {{else}}
              <LinkTo
                @route={{tab.route}}
                class="{{TAB_BASE}} {{if (eq tab.route this.currentRoute) TAB_ACTIVE TAB_INACTIVE}}"
                data-nav-tab
              >
                {{tab.label}}
              </LinkTo>
            {{/if}}
          {{/each}}

          {{#if this.hasOverflow}}
            <Popover @placement="bottom-end" @maxWidth={{220}}>
              <:trigger as |p|>
                <button
                  type="button"
                  class="p-2 tio-fg-muted hover:tio-fg-secondary transition-colors -mb-0.5"
                  aria-label="More tabs"
                  {{p.register}}
                  {{on "click" p.toggle}}
                >
                  <PhDotsThree @size={{20}} />
                </button>
              </:trigger>
              <:body>
                <div class="tio-col-flush py-1" role="menu" aria-label="More tabs">
                  {{#each this.overflowTabs as |tab|}}
                    <button
                      type="button"
                      class="text-left px-4 py-2 text-sm transition-colors
                        {{if
                          (eq tab.route this.currentRoute)
                          'text-violet-600 bg-violet-50 font-medium'
                          'tio-fg-secondary hover:tio-bg-sunken hover:tio-fg-primary'
                        }}"
                      {{on "click" (fn this.handleOverflowClick tab)}}
                    >
                      {{tab.label}}
                    </button>
                  {{/each}}
                </div>
              </:body>
            </Popover>
          {{/if}}
        </nav>
      </div>
    </div>
  </template>
}

export default NavTabs;
